import { type ScreeningAvailbility } from '@components/app/ApplyPackagePage/AvailabilitySelector/helpers'
import { AvailabilitySelectorPrompt } from '@components/app/ApplyPackagePage/AvailabilitySelectorPrompt'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { Tooltip } from '@components/primitives/Tooltip'
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import { toZonedTime } from 'date-fns-tz'
import { useState } from 'react'

export function AvailabilitySelectorQuestion({
  screeningVisitAvailabilities,
  screeningVisitDurationHours,
  trialLocation,
  trialReimbursements,
}: Pick<
  ApplySuccessPageProps,
  | 'screeningVisitAvailabilities'
  | 'screeningVisitDurationHours'
  | 'trialLocation'
  | 'trialReimbursements'
>) {
  // We get this data in the form of Fri Sep 13 2024 08:00:00
  // If the user is in san francisco, I want the result to be Fri Sep 13 2024 08:00:00 PDT
  // If the user is in new york, I want the result to be Fri Sep 13 2024 08:00:00 EDT
  const [selectedSlots, setSelectedSlots] = useState<ScreeningAvailbility[]>(
    screeningVisitAvailabilities.map((slot) => {
      return {
        endTimeISO8601: toZonedTime(slot.endTimeISO8601, 'UTC'),
        startTimeISO8601: toZonedTime(slot.startTimeISO8601, 'UTC'),
      }
    }),
  )

  return (
    <div className='flex flex-col gap-2'>
      <div>
        <Text className='mb-4' element='h1' styleName='h3'>
          What time would work for a{' '}
          <span className='underline'>{screeningVisitDurationHours} hour</span>{' '}
          screening appointment next week?
        </Text>
        <Text>
          Please select 5 different time slots. If the research team is able to
          schedule your appointment at one of these times, you will be notified.
        </Text>

        <Tooltip
          description={
            <ol className='list-decimal p-4 text-white'>
              <li>The research team will review your profile.</li>
              <li>
                Then they’ll review the clinic’s availability for your
                appointment.
              </li>
              <li>
                If one of the times you provided works for them as well, they’ll
                book you in.
              </li>
              <li>
                You’ll receive an email when this appointment is scheduled
              </li>
            </ol>
          }
          descriptionClassName='bg-grey900'
          inlineTooltip={true}
          label={
            <Text className='text-neutral600 underline md:text-right'>
              How booking works on Power
              <Icon className='mx-2 text-neutral600' icon={faQuestionCircle} />
            </Text>
          }
        />
      </div>

      <AvailabilitySelectorPrompt
        screeningVisitDurationHours={screeningVisitDurationHours}
        selectedSlots={selectedSlots}
        setSelectedSlots={setSelectedSlots}
        travelReimbursement={trialReimbursements.find(
          (reimbursement) => reimbursement.type === 'travel',
        )}
        trialLocation={trialLocation}
      />
    </div>
  )
}
